import { useEffect, useState, useCallback } from "react";
import api, { dkk } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Wallet, Plus, Trash2, Check, Pencil, X, TrendingUp, TrendingDown } from "lucide-react";

const FAL = "#FB923C";
const RIN = "#38BDF8";
const GREEN = "#34D399";
const RED = "#F87171";

const EXPENSE_TYPES = ["Husleje", "Løn", "Forsikring", "El, vand & varme", "Leasing", "Abonnementer", "Markedsføring", "Revisor/regnskab", "Andet"];
const TYPE_COLORS = ["#38BDF8", "#FB923C", "#34D399", "#A78BFA", "#F472B6", "#FBBF24", "#22D3EE", "#94A3B8", "#64748B"];
const typeColor = (t) => TYPE_COLORS[Math.max(0, EXPENSE_TYPES.indexOf(t)) % TYPE_COLORS.length];

const MONTHS_DA = ["januar", "februar", "marts", "april", "maj", "juni", "juli", "august", "september", "oktober", "november", "december"];
function monthLabel(iso) {
  if (!iso) return "";
  const [, m] = iso.split("-");
  return MONTHS_DA[parseInt(m, 10) - 1] || iso;
}

function SummaryCard({ title, value, sub, color, testId }) {
  return (
    <div className="rounded-2xl bg-[#ffffff] border border-[#e2e8f0] p-5" data-testid={testId}>
      <h3 className="text-[13px] font-semibold uppercase tracking-[0.14em] text-slate-500 mb-2">{title}</h3>
      <div className="font-heading text-3xl font-bold tabular-nums" style={{ color: color || "#F8FAFC" }}>{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
    </div>
  );
}

function CoverageBar({ block }) {
  const pct = Math.max(0, Math.min(block.coverage_pct ?? 0, 100));
  const covered = (block.coverage_pct ?? 0) >= 100;
  return (
    <div data-testid="coverage-bar">
      <div className="flex items-baseline justify-between mb-1.5">
        <span className="text-sm text-slate-500">Dækning af faste udgifter</span>
        <span className="text-sm font-medium" style={{ color: covered ? GREEN : "#FBBF24" }}>
          {block.coverage_pct == null ? "—" : block.coverage_pct > 100 ? ">100%" : `${block.coverage_pct}%`}
        </span>
      </div>
      <div className="h-2.5 rounded bg-[#e2e8f0] overflow-hidden">
        <div className="h-full rounded transition-all duration-700" style={{ width: `${pct}%`, background: covered ? GREEN : "#FBBF24" }} />
      </div>
      <div className="mt-2 text-xs text-slate-500">
        {block.expenses === 0
          ? "Ingen faste udgifter tastet ind endnu."
          : covered
            ? `Udgifterne er dækket — ${dkk(block.surplus)} i overskud.`
            : `Mangler ${dkk(block.remaining)} i fortjeneste for at dække udgifterne.`}
      </div>
    </div>
  );
}

export function FinanceStats({ store }) {
  const [overview, setOverview] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newStore, setNewStore] = useState("falster");
  const [newName, setNewName] = useState("");
  const [newAmount, setNewAmount] = useState("");
  const [newType, setNewType] = useState("Husleje");
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState("");
  const [editAmount, setEditAmount] = useState("");
  const [editType, setEditType] = useState("Andet");

  const scope = store === "falster" || store === "ringsted" ? store : "all";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [o, e] = await Promise.all([api.get("/finance/overview"), api.get("/expenses")]);
      setOverview(o.data); setExpenses(e.data);
    } catch { toast.error("Kunne ikke hente økonomidata"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (scope !== "all") setNewStore(scope); }, [scope]);

  const parseAmount = (v) => parseFloat(String(v).replace(/\./g, "").replace(",", "."));

  const addExpense = async () => {
    const amt = parseAmount(newAmount);
    if (!newName.trim()) { toast.error("Indtast et navn"); return; }
    if (!amt || amt <= 0) { toast.error("Indtast et gyldigt beløb"); return; }
    try {
      await api.post("/expenses", { store: newStore, name: newName.trim(), amount: amt, type: newType });
      setNewName(""); setNewAmount(""); await load(); toast.success("Udgift tilføjet");
    } catch { toast.error("Kunne ikke gemme udgift"); }
  };

  const saveEdit = async (id) => {
    const amt = parseAmount(editAmount);
    if (!editName.trim() || !amt || amt <= 0) { toast.error("Ugyldige værdier"); return; }
    try {
      await api.put(`/expenses/${id}`, { name: editName.trim(), amount: amt, type: editType });
      setEditId(null); await load(); toast.success("Udgift opdateret");
    } catch { toast.error("Kunne ikke opdatere"); }
  };

  const remove = async (id) => {
    try { await api.delete(`/expenses/${id}`); await load(); toast.success("Udgift slettet"); }
    catch { toast.error("Kunne ikke slette"); }
  };

  if (loading || !overview) {
    return <p className="text-slate-500 text-sm py-8 text-center" data-testid="finance-loading">Henter…</p>;
  }

  const block = scope === "all" ? overview.combined : overview.stores[scope];
  const surplusPositive = (block.surplus ?? 0) >= 0;
  const visibleExpenses = scope === "all" ? expenses : expenses.filter((e) => e.store === scope);
  const scopeLabel = scope === "all" ? "Begge butikker" : (scope === "falster" ? "Falster" : "Ringsted");

  const byType = {};
  visibleExpenses.forEach((e) => { const t = e.type || "Andet"; byType[t] = (byType[t] || 0) + (e.amount || 0); });
  const typeRows = Object.entries(byType).map(([type, amount]) => ({ type, amount })).sort((a, b) => b.amount - a.amount);
  const maxType = Math.max(...typeRows.map((t) => t.amount), 1);

  return (
    <div className="space-y-4" data-testid="finance-stats">
      <div className="flex items-center gap-2 text-sm text-slate-500" data-testid="finance-header">
        <Wallet className="w-4 h-4" />
        <span>Måned-til-dato · {monthLabel(overview.month_start)} · {scopeLabel}</span>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <SummaryCard title="Månedens overskud" value={dkk(block.surplus)}
          color={surplusPositive ? GREEN : RED}
          sub={surplusPositive ? "Efter faste udgifter" : "Underskud efter udgifter"} testId="finance-surplus" />
        <SummaryCard title="Fortjeneste (mtd)" value={dkk(block.profit)}
          sub={`Omsætning ${dkk(block.revenue)}`} testId="finance-profit" />
        <SummaryCard title="Faste udgifter" value={dkk(block.expenses)}
          sub={`${visibleExpenses.length} poster`} testId="finance-expenses-total" />
        <SummaryCard title="Mangler at dække" value={block.remaining > 0 ? dkk(block.remaining) : "0 kr."}
          color={block.remaining > 0 ? "#FBBF24" : GREEN}
          sub={block.remaining > 0 ? "Mangler for at dække udgifterne" : "Udgifter dækket"} testId="finance-remaining" />
      </div>

      {/* Coverage */}
      <div className="rounded-2xl bg-[#ffffff] border border-[#e2e8f0] p-5">
        <CoverageBar block={block} />
        <div className="mt-4 pt-4 border-t border-[#e2e8f0] flex items-center gap-2 text-sm">
          {surplusPositive
            ? <TrendingUp className="w-4 h-4" style={{ color: GREEN }} />
            : <TrendingDown className="w-4 h-4" style={{ color: RED }} />}
          <span className="text-slate-500">
            Fortjeneste {dkk(block.profit)} − faste udgifter {dkk(block.expenses)} ={" "}
            <span className="font-semibold" style={{ color: surplusPositive ? GREEN : RED }}>{dkk(block.surplus)}</span>
          </span>
        </div>
      </div>

      {/* Per-store breakdown when viewing combined */}
      {scope === "all" && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="finance-per-store">
          {["falster", "ringsted"].map((s) => {
            const b = overview.stores[s];
            const pos = (b.surplus ?? 0) >= 0;
            return (
              <div key={s} className="rounded-2xl bg-[#ffffff] border border-[#e2e8f0] p-5" data-testid={`finance-store-${s}`}>
                <div className="flex items-center gap-2 mb-3">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ background: s === "falster" ? FAL : RIN }} />
                  <span className="font-medium text-slate-700">{b.name}</span>
                </div>
                <div className="space-y-1.5 text-sm">
                  <div className="flex justify-between"><span className="text-slate-500">Fortjeneste</span><span className="tabular-nums text-slate-800">{dkk(b.profit)}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Faste udgifter</span><span className="tabular-nums text-slate-800">{dkk(b.expenses)}</span></div>
                  <div className="flex justify-between pt-1.5 border-t border-[#e2e8f0]"><span className="text-slate-500">Overskud</span><span className="tabular-nums font-semibold" style={{ color: pos ? GREEN : RED }}>{dkk(b.surplus)}</span></div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Fordeling pr. type */}
      {typeRows.length > 0 && (
        <div className="rounded-2xl bg-[#ffffff] border border-[#e2e8f0] p-4 sm:p-5" data-testid="expense-type-breakdown">
          <h3 className="text-sm font-semibold uppercase tracking-[0.14em] text-slate-500 mb-3">Fordeling pr. type</h3>
          <ul className="space-y-2.5">
            {typeRows.map((r) => (
              <li key={r.type} data-testid={`expense-type-${r.type}`}>
                <div className="flex items-center justify-between text-sm mb-1">
                  <span className="flex items-center gap-2 text-slate-500">
                    <span className="w-2.5 h-2.5 rounded-full" style={{ background: typeColor(r.type) }} />{r.type}
                  </span>
                  <span className="font-medium tabular-nums text-slate-800">{dkk(r.amount)}</span>
                </div>
                <div className="h-1.5 rounded bg-[#e2e8f0] overflow-hidden">
                  <div className="h-full rounded" style={{ width: `${(r.amount / maxType) * 100}%`, background: typeColor(r.type) }} />
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Expense editor */}
      <div className="rounded-2xl bg-[#ffffff] border border-[#e2e8f0] p-4 sm:p-5" data-testid="expense-editor">
        <h3 className="text-sm font-semibold uppercase tracking-[0.14em] text-slate-500 mb-1">Faste månedlige udgifter</h3>
        <p className="text-xs text-slate-500 mb-4">Indtast beløb <span className="text-slate-500">ekskl. moms</span> (nettobeløb) — overskuddet beregnes på fortjeneste ekskl. moms.</p>

        {/* Add row */}
        <div className="flex flex-col sm:flex-row flex-wrap gap-2 mb-4">
          <Select value={newStore} onValueChange={setNewStore}>
            <SelectTrigger className="h-9 rounded-lg bg-[#f8fafc] border-[#e2e8f0] text-slate-700 sm:w-[130px]" data-testid="expense-store-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="rounded-lg bg-[#ffffff] border-[#e2e8f0] text-slate-700">
              <SelectItem value="falster">Falster</SelectItem>
              <SelectItem value="ringsted">Ringsted</SelectItem>
            </SelectContent>
          </Select>
          <Select value={newType} onValueChange={setNewType}>
            <SelectTrigger className="h-9 rounded-lg bg-[#f8fafc] border-[#e2e8f0] text-slate-700 sm:w-[160px]" data-testid="expense-type-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="rounded-lg bg-[#ffffff] border-[#e2e8f0] text-slate-700">
              {EXPENSE_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Beskrivelse (fx Butik Falster)"
            className="h-9 rounded-lg bg-[#f8fafc] border-[#e2e8f0] text-slate-800 flex-1 min-w-[150px]" data-testid="expense-name-input" />
          <Input value={newAmount} onChange={(e) => setNewAmount(e.target.value)} placeholder="Beløb / md."
            onKeyDown={(e) => e.key === "Enter" && addExpense()}
            className="h-9 rounded-lg bg-[#f8fafc] border-[#e2e8f0] text-slate-800 sm:w-[140px]" data-testid="expense-amount-input" />
          <Button onClick={addExpense} className="h-9 rounded-lg bg-[#38BDF8] hover:bg-sky-400 text-[#0b1120] gap-1.5" data-testid="expense-add-btn">
            <Plus className="w-4 h-4" /> Tilføj
          </Button>
        </div>

        {visibleExpenses.length === 0 ? (
          <p className="text-slate-500 text-sm py-6 text-center" data-testid="expenses-empty">Ingen faste udgifter tastet ind endnu.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[480px]" data-testid="expenses-table">
              <thead>
                <tr className="text-slate-500 text-xs uppercase tracking-wider border-b border-[#e2e8f0]">
                  <th className="text-left font-medium py-2 pr-2">Udgift</th>
                  <th className="text-left font-medium py-2 pr-2">Type</th>
                  {scope === "all" && <th className="text-left font-medium py-2 pr-2">Butik</th>}
                  <th className="text-right font-medium py-2 pr-2">Beløb / md.</th>
                  <th className="text-right font-medium py-2 w-24">Handling</th>
                </tr>
              </thead>
              <tbody>
                {visibleExpenses.map((e, i) => (
                  <tr key={e.id} className="border-b border-[#e2e8f0]" data-testid={`expense-row-${i}`}>
                    {editId === e.id ? (
                      <>
                        <td className="py-2 pr-2"><Input value={editName} onChange={(ev) => setEditName(ev.target.value)} className="h-8 rounded-md bg-[#f8fafc] border-[#e2e8f0] text-slate-800" data-testid={`expense-edit-name-${i}`} /></td>
                        <td className="py-2 pr-2">
                          <Select value={editType} onValueChange={setEditType}>
                            <SelectTrigger className="h-8 rounded-md bg-[#f8fafc] border-[#e2e8f0] text-slate-700 w-[150px]" data-testid={`expense-edit-type-${i}`}><SelectValue /></SelectTrigger>
                            <SelectContent className="rounded-lg bg-[#ffffff] border-[#e2e8f0] text-slate-700">
                              {EXPENSE_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </td>
                        {scope === "all" && <td className="py-2 pr-2 text-slate-500 capitalize">{e.store}</td>}
                        <td className="py-2 pr-2"><Input value={editAmount} onChange={(ev) => setEditAmount(ev.target.value)} className="h-8 w-28 ml-auto rounded-md bg-[#f8fafc] border-[#e2e8f0] text-slate-800 text-right" data-testid={`expense-edit-amount-${i}`} /></td>
                        <td className="py-2 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button size="icon" className="h-7 w-7 rounded-md bg-[#38BDF8] hover:bg-sky-400 text-[#0b1120]" onClick={() => saveEdit(e.id)} data-testid={`expense-save-${i}`}><Check className="w-4 h-4" /></Button>
                            <Button variant="ghost" size="icon" className="h-7 w-7 rounded-md text-slate-500" onClick={() => setEditId(null)}><X className="w-4 h-4" /></Button>
                          </div>
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="py-2.5 pr-2 text-slate-700">{e.name}</td>
                        <td className="py-2.5 pr-2">
                          <span className="inline-flex items-center gap-1.5 text-xs text-slate-500">
                            <span className="w-2 h-2 rounded-full" style={{ background: typeColor(e.type || "Andet") }} />{e.type || "Andet"}
                          </span>
                        </td>
                        {scope === "all" && <td className="py-2.5 pr-2 text-slate-500 capitalize">{e.store}</td>}
                        <td className="py-2.5 pr-2 text-right tabular-nums text-slate-800">{dkk(e.amount)}</td>
                        <td className="py-2.5 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <button className="text-slate-500 hover:text-slate-900 p-1" onClick={() => { setEditId(e.id); setEditName(e.name); setEditAmount(String(Math.round(e.amount))); setEditType(e.type || "Andet"); }} data-testid={`expense-edit-${i}`}><Pencil className="w-3.5 h-3.5" /></button>
                            <button className="text-slate-500 hover:text-[#dc2626] p-1" onClick={() => remove(e.id)} data-testid={`expense-delete-${i}`}><Trash2 className="w-3.5 h-3.5" /></button>
                          </div>
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="text-slate-500 font-medium border-t border-[#e2e8f0]">
                  <td className="py-2.5">I alt</td>
                  <td></td>
                  {scope === "all" && <td></td>}
                  <td className="py-2.5 text-right tabular-nums" data-testid="expenses-total-row">{dkk(visibleExpenses.reduce((s, e) => s + (e.amount || 0), 0))}</td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
